import { createAuthClient } from "better-auth/react";
import { emailOTPClient } from "better-auth/client/plugins";
import { useEffect, useState } from "react";
import { FEATURE_FLAGS } from "./config";

let _accessToken: string | null = null;
let _refreshPromise: Promise<string | null> | null = null;
let _isReady = false;
const _readyListeners: Array<() => void> = [];

export const onSyncReady = (cb: () => void) => {
    if (_isReady) cb();
    else _readyListeners.push(cb);
};

const triggerReady = () => {
    _isReady = true;
    _readyListeners.forEach(cb => cb());
};

const _tokenListeners: Array<(token: string | null) => void> = [];

export const setTokens = (access: string | null) => {
    _accessToken = access;
    _tokenListeners.forEach(cb => cb(access));
    triggerReady();
};

export const getAccessToken = () => _accessToken;

/**
 * Refresh the access token using the httpOnly refresh cookie
 * Includes a concurrency lock to prevent parallel refresh race conditions.
 */
export const refreshAccessToken = async () => {
    if (_refreshPromise) return _refreshPromise;

    _refreshPromise = (async () => {
        try {
            const base = getBaseURL();
            const url = `${base}${base.endsWith('/') ? '' : '/'}api/v1/auth/refresh`;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}), // Send empty object to avoid 400 on empty body parsers
            });
            if (res.ok) {
                const data = await res.json();
                setTokens(data.accessToken);
                return data.accessToken;
            } else {
                _accessToken = null;
            }
        } catch (e) {
            console.error("[Auth] Token refresh failed:", e);
        } finally {
            _refreshPromise = null;
        }
        return null;
    })();

    return _refreshPromise;
};

const getBaseURL = () => {
    if (typeof window === 'undefined') {
        const base = process.env.NEXT_PUBLIC_API_URL || "https://drop-backend-65296643664.us-central1.run.app";
        return base;
    }
    return window.location.origin;
};

// Log for debugging (this will show in user's console)
if (typeof window !== 'undefined') {
    console.log("[AuthClient] Initializing with baseURL:", getBaseURL());
}

export const authClient = createAuthClient({
    baseURL: getBaseURL(),
    basePath: "/api/auth",
    plugins: [
        emailOTPClient()
    ],
    fetchOptions: {
        onRequest: async (context) => {
            if (_accessToken) {
                context.headers.set('Authorization', `Bearer ${_accessToken}`);
            }
            return context;
        },
        onError: async (context) => {
            const { response, request } = context;
            const url = typeof request.url === 'string' ? request.url : request.url.toString();
            if (response.status === 401 && !url.includes('/api/v1/auth')) {
                console.warn("[AuthClient] 401 Detected. Refreshing...");
                await refreshAccessToken();
                // Note: We don't reload anymore. Components using authenticatedFetch will 
                // handle their own retries. Better-auth internal calls will need to be 
                // manual retries if they fail, but high-level reloads are too disruptive.
            }
        },
    }
});

/**
 * Migration Helper: Exchange better-auth session for JWT
 */
export const syncSession = async () => {
    try {
        const base = getBaseURL();
        const url = `${base}${base.endsWith('/') ? '' : '/'}api/v1/auth/exchange`;
        const res = await fetch(url, { method: 'POST' });
        if (res.ok) {
            const data = await res.json();
            setTokens(data.accessToken);
            return data.user;
        }
    } catch (e) {
        console.error("[Auth] Session sync failed:", e);
    }
    return null;
};

/**
 * Enhanced signOut to clear custom cookies
 */
const originalSignOut = authClient.signOut;
export const signOut = async (options?: any) => {
    try {
        const base = getBaseURL();
        const url = `${base}${base.endsWith('/') ? '' : '/'}api/v1/auth/logout`;
        await fetch(url, { method: 'POST' });
    } catch (e) {
        console.error("[Auth] Custom logout failed:", e);
    }
    _accessToken = null;
    return await originalSignOut(options);
};

export const skipSync = () => triggerReady();

export const { signIn, signUp, useSession } = authClient;

/**
 * Robust Data Fetcher for Protected API Routes
 * - Normalizes paths (removes double prefixes)
 * - Injects JWT Bearer token
 * - Handles auto-refresh on 401
 */
export const authenticatedFetch = async (path: string, options: RequestInit = {}) => {
    // 1. Skip sync if auth is disabled
    if (!FEATURE_FLAGS.ENABLE_AUTH) {
        // Just normalize path and return
    } else {
        // Wait for Sync if we know we should have a token but don't yet
        const session = await authClient.getSession();
        const hasSession = !!session?.data?.user;
        if (hasSession && !_accessToken && _isReady) {
            console.log("[Auth] Session detected but no token. Waiting for sync...");
            await new Promise<void>(resolve => {
                const check = () => {
                    if (_accessToken || !_isReady) resolve();
                    else setTimeout(check, 50);
                };
                check();
            });
        }
    }

    // 2. Normalize Path (Remove accidental /api/auth or duplicate /api)
    let cleanPath = path.startsWith('/') ? path : `/${path}`;
    if (cleanPath.startsWith('/api/auth/api/v1/')) {
        cleanPath = cleanPath.replace('/api/auth/api/v1/', '/api/v1/');
    }

    const base = getBaseURL();
    const url = `${base}${cleanPath.startsWith('/') ? '' : '/'}${cleanPath}`;

    // 2. Prepare Headers
    const headers = new Headers(options.headers || {});
    if (_accessToken) {
        headers.set('Authorization', `Bearer ${_accessToken}`);
    }
    if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
    }

    // 3. Execute Fetch
    const response = await fetch(url, { ...options, headers });

    // 4. Handle 401 (Unauthorized) Retry
    if (response.status === 401 && !url.includes('/api/v1/auth')) {
        console.warn(`[Auth] 401 detected on ${path}. Token present: ${!!_accessToken}. Attempting refresh...`);
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers.set('Authorization', `Bearer ${newToken}`);
            return await fetch(url, { ...options, headers });
        }
    }

    return response;
};

/**
 * Unified Identity Hook
 * Reactively provides the current user (better-auth OR guest JWT)
 */
export const useActiveUser = () => {
    const { data: session, isPending } = useSession();
    const [jwtUser, setJwtUser] = useState<any>(null);
    const [jwtLoading, setJwtLoading] = useState(!!_accessToken);
    const [currentToken, setCurrentToken] = useState<string | null>(_accessToken);

    // Listen for token changes
    useEffect(() => {
        const handler = (token: string | null) => {
            setCurrentToken(token);
            if (token) setJwtLoading(true);
            else {
                setJwtUser(null);
                setJwtLoading(false);
            }
        };
        _tokenListeners.push(handler);
        return () => {
            const idx = _tokenListeners.indexOf(handler);
            if (idx > -1) _tokenListeners.splice(idx, 1);
        };
    }, []);

    useEffect(() => {
        // 1. If better-auth is still loading, wait
        if (isPending) return;

        // 2. If better-auth has a user, we use that (standard flow)
        if (session?.user) {
            setJwtUser(null);
            setJwtLoading(false);
            return;
        }

        // 3. If no better-auth, check if we have a JWT token (guest flow)
        if (!currentToken) {
            setJwtUser(null);
            setJwtLoading(false);
            return;
        }

        // 4. If we have a token but no user yet, or token changed, fetch
        let isMounted = true;
        setJwtLoading(true);
        const fetchMe = async () => {
            try {
                const res = await fetch(`${getBaseURL()}/api/v1/auth/me`, {
                    headers: { Authorization: `Bearer ${currentToken}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (isMounted) setJwtUser(data.user);
                } else {
                    if (isMounted) setJwtUser(null);
                }
            } catch (e) {
                console.error("[useActiveUser] Fetch failed:", e);
            } finally {
                if (isMounted) setJwtLoading(false);
            }
        };

        fetchMe();
        return () => { isMounted = false; };
    }, [session, isPending, currentToken]);

    const loading = isPending || (!!currentToken && jwtLoading && !jwtUser);

    return {
        user: session?.user || jwtUser,
        isPending: loading,
        isAuthenticated: !!(session?.user || currentToken)
    };
};
